<template>
  <div style="position: absolute; left: -9999px; top: -9999px;">
    <table :id="tableId" :style="tableStyle">
      <thead>
        <tr><th colspan="2" :style="titleStyle">{{ item ? item.name : '' }}</th></tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in printRows" :key="idx">
          <td :style="valueStyle"><template v-if="row.col1"><b>{{ row.col1.label }}: </b>{{ printValue(row.col1) }}</template></td>
          <td :style="valueStyle"><template v-if="row.col2"><b>{{ row.col2.label }}: </b>{{ printValue(row.col2) }}</template></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script>
import { resolveAttributeValue, effectiveManualValue, htmlToDocxBlob } from './declarationsService.js'
import { initPdf, initPdfLandscape, setPageNumber } from '@/modules/all-reports/pdf/config.js'

export default {
  name: 'DeclarationPrintTable',
  props: {
    item: { type: Object, required: true },
    fields: { type: Array, required: true },
    tableId: { type: String, required: true },
    settings: {
      type: Object,
      default: () => ({ font_size: 11, orientation: 'portrait', border: true, border_style: 'square' })
    }
  },
  computed: {
    // Used only for the DOCX export (html-docx-js reads this table's inline
    // CSS directly). PDF export goes through generate() below, which draws
    // the border itself with doc.rect()/roundedRect() — jspdf-autotable's
    // useCss mode did not reliably pick up a border defined on the outer
    // <table> tag (same fix as DeclarationCatalogPrintTable.vue).
    tableStyle () {
      const border = this.settings.border ? '1.5pt solid #263238' : 'none'
      const borderRadius = (this.settings.border && this.settings.border_style === 'rounded') ? '8pt' : '0pt'
      return `border: ${border}; border-radius: ${borderRadius}; border-collapse: collapse; margin-bottom: 14pt; width: 100%;`
    },
    titleStyle () {
      return `font-size: ${this.settings.font_size + 3}pt; font-weight: bold; text-align: center; padding: 6pt 8pt; background-color: #F1F5F9; border-bottom: 0.75pt solid #263238;`
    },
    valueStyle () {
      return `font-size: ${this.settings.font_size}pt; padding: 4pt 8pt; vertical-align: top;`
    },
    printRows () {
      const visibleFor = column => this.fields
        .filter(f => f.column === column && f.printaj && !f.removed && this.printValue(f) !== '')
        .sort((a, b) => a.order - b.order)
      const col1 = visibleFor(1)
      const col2 = visibleFor(2)
      const rowCount = Math.max(col1.length, col2.length)
      const rows = []
      for (let i = 0; i < rowCount; i++) {
        rows.push({ col1: col1[i] || null, col2: col2[i] || null })
      }
      return rows
    }
  },
  methods: {
    printValue (field) {
      return field.type === 'attribute' ? resolveAttributeValue(this.item, field.attribute_field) : effectiveManualValue(field)
    },
    // Same wrapping approach as DeclarationCatalogPrintTable.vue's buildFieldLines
    // - value starts beside the bold label if there's room, remainder wraps
    // onto full-width lines below.
    buildFieldLines (doc, label, value, cellWidth, fontSize) {
      doc.setFontSize(fontSize)
      doc.setFont('Roboto-Bold', 'bold')
      const labelText = `${label}: `
      const labelWidth = doc.getTextWidth(labelText)
      doc.setFont('Roboto-Regular', 'normal')
      const besideWidth = cellWidth - labelWidth
      const valueText = String(value)
      const lines = []

      if (besideWidth >= cellWidth * 0.3) {
        const narrowLines = doc.splitTextToSize(valueText, besideWidth)
        const firstLine = narrowLines[0] || ''
        lines.push({ label: labelText, labelWidth, value: firstLine })
        const remainder = valueText.slice(firstLine.length).trim()
        if (remainder) {
          doc.splitTextToSize(remainder, cellWidth).forEach(text => lines.push({ label: null, labelWidth: 0, value: text }))
        }
      } else {
        lines.push({ label: labelText, labelWidth, value: '' })
        doc.splitTextToSize(valueText, cellWidth).forEach(text => lines.push({ label: null, labelWidth: 0, value: text }))
      }

      return lines
    },
    async generate () {
      const doc = this.settings.orientation === 'landscape' ? await initPdfLandscape() : await initPdf()
      const pageWidth = doc.internal.pageSize.getWidth()
      const marginLeft = 40
      const marginRight = 40
      const contentWidth = pageWidth - marginLeft - marginRight
      const borderColor = [38, 50, 56]
      const startY = 40

      const cellPaddingH = 8
      const halfWidth = contentWidth / 2
      const halfTextWidth = halfWidth - cellPaddingH * 2
      const lineHeightFactor = doc.getLineHeightFactor()
      const wrapLineHeight = this.settings.font_size / doc.internal.scaleFactor * lineHeightFactor

      // Styled entirely via autoTable's own JS options, not CSS/useCss — see
      // tableStyle comment above. Cells hold invisible placeholder text sized
      // to the exact line count so autoTable reserves the right row height;
      // didDrawCell then paints the real wrapped "Label: Value" text on top.
      doc.autoTable({
        html: `#${this.tableId}`,
        startY,
        margin: { top: 10, left: marginLeft, right: marginRight, bottom: 10 },
        theme: 'plain',
        tableWidth: contentWidth,
        styles: {
          font: 'Roboto-Regular',
          fontSize: this.settings.font_size,
          cellPadding: { top: 4, right: cellPaddingH, bottom: 4, left: cellPaddingH },
          valign: 'top'
        },
        headStyles: {
          font: 'Roboto-Bold',
          fontStyle: 'bold',
          fontSize: this.settings.font_size + 3,
          halign: 'center',
          fillColor: [241, 245, 249],
          textColor: borderColor,
          cellPadding: { top: 6, right: cellPaddingH, bottom: 6, left: cellPaddingH }
        },
        columnStyles: {
          0: { cellWidth: halfWidth },
          1: { cellWidth: halfWidth }
        },
        didParseCell: (data) => {
          if (data.section !== 'body') return
          data.cell.styles.textColor = [255, 255, 255]
          const row = this.printRows[data.row.index]
          const pair = data.column.index === 0 ? row.col1 : row.col2
          if (!pair) { data.cell.text = ['']; return }
          const value = this.printValue(pair)
          const lineCount = this.buildFieldLines(doc, pair.label, value, halfTextWidth, this.settings.font_size).length
          data.cell.text = new Array(lineCount).fill('')
        },
        didDrawCell: (data) => {
          if (data.section !== 'body') return
          const row = this.printRows[data.row.index]
          const pair = data.column.index === 0 ? row.col1 : row.col2
          if (!pair) return
          const value = this.printValue(pair)
          const lines = this.buildFieldLines(doc, pair.label, value, halfTextWidth, this.settings.font_size)
          const x = data.cell.x + data.cell.padding('left')
          const baseY = data.cell.y + data.cell.padding('top') + this.settings.font_size * 0.8
          doc.setFontSize(this.settings.font_size)
          doc.setTextColor(...borderColor)
          lines.forEach((line, idx) => {
            const lineY = baseY + idx * wrapLineHeight
            if (line.label) {
              doc.setFont('Roboto-Bold', 'bold')
              doc.text(line.label, x, lineY)
            }
            doc.setFont('Roboto-Regular', 'normal')
            doc.text(line.value, x + line.labelWidth, lineY)
          })
        }
      })
      doc.setTextColor(0, 0, 0)

      const endY = doc.lastAutoTable.finalY

      if (this.settings.border) {
        doc.setDrawColor(...borderColor)
        doc.setLineWidth(0.75)
        if (this.settings.border_style === 'rounded') {
          doc.roundedRect(marginLeft, startY, contentWidth, endY - startY, 4, 4)
        } else {
          doc.rect(marginLeft, startY, contentWidth, endY - startY)
        }
      }

      await setPageNumber(doc)
      return doc.output('datauristring').split(',')[1]
    },
    generateDocx () {
      return htmlToDocxBlob(document.getElementById(this.tableId).outerHTML)
    }
  }
}
</script>
